import {
  FC,
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
  memo,
  useMemo
} from "react"
import { PanelType } from "../../../types"
import { useAppDispatch, useAppSelector } from "../../hooks"
import {
  resetVisiblePanel,
  setPanelContext,
  selectPanelContext
} from "../../../redux/slices/app"
import If from "../if"

// Lazy-loaded panels (code-splitting)
const CreateOrganizationPanel = lazy(() => import("../organizations/create"))
const ManageBrand = lazy(() => import("../brands/manage"))
const EditLoProfile = lazy(() => import("../account/edit-lo-profile"))
const ManageEngagementPanel = lazy(() => import("../engagements/manage"))
const ManageDreamRatePanel = lazy(
  () => import("../dream-rate/form/manage-panel")
)
const ManageConnector = lazy(
  () => import("../config-admin/config-connectors/manage")
)

type PanelRenderer = (args: {
  open: boolean
  onClose: () => void
  panelContext: { orgId?: string } | null
}) => JSX.Element

const panelRegistry: Partial<Record<PanelType, PanelRenderer>> = {
  "organization/new": ({ open, onClose }) => (
    <CreateOrganizationPanel open={open} onClose={onClose} />
  ),
  "brand/new": ({ open, onClose, panelContext }) => (
    <ManageBrand
      open={open}
      isInSidePanel
      onClose={onClose}
      orgId={panelContext?.orgId}
    />
  ),
  "loan-officer/new": ({ open, onClose }) => (
    <EditLoProfile open={open} isInSidePanel onClose={onClose} />
  ),
  "engagement/new": ({ open, onClose }) => (
    <ManageEngagementPanel open={open} onClose={onClose} nonqmEnabled />
  ),
  "dream-rate/new": ({ open, onClose }) => (
    <ManageDreamRatePanel open={open} onClose={onClose} />
  ),
  "connector/new": ({ open, onClose }) => (
    <ManageConnector open={open} brandId="" onClose={onClose} />
  ),
  "connector/edit": ({ open, onClose }) => (
    <ManageConnector open={open} brandId="" onClose={onClose} />
  )
}

interface Props {
  visiblePanel: PanelType | null
}

const CLOSE_DELAY_MS = 500

const PanelsGroup: FC<Props> = ({ visiblePanel }) => {
  const dispatch = useAppDispatch()
  const panelContext = useAppSelector(selectPanelContext)
  const handleReset = useCallback(() => {
    dispatch(resetVisiblePanel({}))
    dispatch(setPanelContext(null))
  }, [dispatch])

  const [mounted, setMounted] = useState(false)
  const [currentPanel, setCurrentPanel] = useState<PanelType | null>(null)
  const [open, setOpen] = useState(false)

  const timers = useRef<{
    open?: number
    close?: number
    openRaf1?: number
    openRaf2?: number
  }>({})

  useEffect(() => {
    const pending = timers.current
    return () => {
      if (pending.open) window.clearTimeout(pending.open)
      if (pending.close) window.clearTimeout(pending.close)
      if (pending.openRaf1) window.cancelAnimationFrame(pending.openRaf1)
      if (pending.openRaf2) window.cancelAnimationFrame(pending.openRaf2)
    }
  }, [])

  useEffect(() => {
    if (visiblePanel !== null) {
      setMounted(true)
      setCurrentPanel(visiblePanel)
      // Delay flipping `open` until after the next paint so the initial enter animation runs
      // Use double rAF which is more reliable than setTimeout(0)
      timers.current.openRaf1 = window.requestAnimationFrame(() => {
        timers.current.openRaf2 = window.requestAnimationFrame(() => {
          setOpen(true)
        })
      })
    } else {
      setOpen(false)
      timers.current.close = window.setTimeout(() => {
        setMounted(false)
        setCurrentPanel(null)
      }, CLOSE_DELAY_MS)
    }
  }, [visiblePanel])

  const renderPanel = useMemo(() => {
    if (!currentPanel) return null
    const renderer = panelRegistry[currentPanel]
    if (!renderer) return null
    return renderer({ open, onClose: handleReset, panelContext })
  }, [currentPanel, open, handleReset, panelContext])

  return (
    <If
      condition={mounted}
      then={<Suspense fallback={null}>{renderPanel}</Suspense>}
    />
  )
}

export default memo(PanelsGroup)
